import { DateTime } from 'luxon';

/**
 * Session type that includes the new one-off session fields
 */
export interface SessionWithOneOff {
  sessionId?: string; // Optional to handle new sessions being created
  isOneOff?: boolean;
  activityDate?: string | null;
  [key: string]: any; // Allow other session properties
}

/**
 * Filters sessions to include only those that should appear on a specific activity date.
 * Returns regular sessions (not one-off) plus any one-off sessions for the specific date.
 * 
 * @param sessions - Array of sessions to filter
 * @param activityDate - The activity date to filter for (ISO date string)
 * @returns Filtered array of sessions
 */
export function filterSessionsByActivityDate<T extends SessionWithOneOff>(
  sessions: T[], 
  activityDate: string
): T[] {
  return sessions.filter((session) => {
    // Include regular sessions (not one-off)
    if (!session.isOneOff) {
      return true;
    }
    
    // For one-off sessions, only include if they match the activity date
    if (session.isOneOff && session.activityDate) {
      // Normalize both dates to ensure consistent comparison
      const sessionDate = DateTime.fromISO(session.activityDate).toISODate();
      const targetDate = DateTime.fromISO(activityDate).toISODate();
      return sessionDate === targetDate;
    }
    
    // Exclude one-off sessions without an activity date
    return false;
  });
}

/**
 * Checks if a session is a one-off session
 * 
 * @param session - Session to check
 * @returns True if the session is a one-off session
 */
export function isOneOffSession(session: SessionWithOneOff): boolean {
  return Boolean(session?.isOneOff);
}

/**
 * Gets the display name for a session, adding a badge for one-off sessions
 * 
 * @param session - Session to get display name for
 * @returns Display name with optional one-off indicator
 */
export function getSessionDisplayName(session: SessionWithOneOff): string {
  const baseName = session.name || 'Unnamed Session';
  return session.isOneOff ? `${baseName} (One-Off)` : baseName;
}